import { cn } from '@/lib/utils'

export type ProgressBarSize = 'sm' | 'md'
export type ProgressBarTone = 'info' | 'success'

export interface ProgressBarProps {
  answered: number
  total: number
  /** Accessible name, e.g. "Fleet Directives progress". */
  label: string
  size?: ProgressBarSize
  tone?: ProgressBarTone
  /** Caption alignment under the track. */
  align?: 'left' | 'right'
  /** Renders the compact answered/total caption under the track. Default true;
   * set false when the caller renders that count itself (e.g. beside a heading
   * above the bar) so it isn't shown twice. */
  caption?: boolean
  className?: string
}

const TRACK_HEIGHT: Record<ProgressBarSize, string> = { sm: 'h-1', md: 'h-1.5' }
const FILL_TONE: Record<ProgressBarTone, string> = {
  info: 'bg-info-surface',
  success: 'bg-success',
}

/**
 * The one progress-bar rendering in the app: a proportional fill with a compact
 * answered/total caption. A zero total renders an empty track and "0/0".
 */
export function ProgressBar({
  answered,
  total,
  label,
  size = 'md',
  tone = 'info',
  align = 'left',
  caption = true,
  className,
}: ProgressBarProps) {
  const percent = total > 0 ? Math.round((answered / total) * 100) : 0
  return (
    <div className={className}>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={`${answered} of ${total} answered`}
        className={cn('overflow-hidden rounded-tiny bg-surface-hover', TRACK_HEIGHT[size])}
      >
        <div
          className={cn('h-full rounded-tiny transition-[width] duration-300', FILL_TONE[tone])}
          style={{ width: `${percent}%` }}
        />
      </div>
      {caption && (
        <div className={cn('mt-1 text-xs text-subtle', align === 'right' && 'text-right')}>
          {`${answered}/${total}`}
        </div>
      )}
    </div>
  )
}
